import { cls } from "@utdanningsdirektoratet/utils/cls";
import { Tooltip } from "@utdanningsdirektoratet/tooltip";
import { FormattedMessage } from "react-intl";
import { useForm } from "@utdanningsdirektoratet/form";
import SvarAlternativ from "./SvarAlternativ";
import Obligatorisk from "./Obligatorisk";
import {
  EttFlereValgMulighetViewModel,
  SpørsmålUttalelseViewModel,
} from "../../../../../ApiClients";
import {
  getSpørsmålFormId,
  SpørsmålFormId,
} from "../../../../../utils/hoeringUtils";
import { useUttalelse } from "../../../../../hooks";

const EttValg = ({
  ettValg,
  id,
  readonly,
  obligatorisk,
  forhandsvisning,
}: {
  readonly: boolean;
  obligatorisk: boolean;
  forhandsvisning: boolean;
  id: number;
  ettValg: EttFlereValgMulighetViewModel;
}) => {
  const { data: uttalelse } = useUttalelse();
  const { svarAlternativ } = ettValg;

  const { fields = {} } =
    useForm<
      Record<
        SpørsmålFormId,
        SpørsmålUttalelseViewModel["svarPåSvarAlternativer"]
      >
    >();

  const field = fields[getSpørsmålFormId(id, "svarPåSvarAlternativer")] ?? {
    value: uttalelse?.spørsmål.find((s) => s.spørsmålId === id)
      ?.svarPåSvarAlternativer,
  };

  const ettvalgId = ettValg.svarAlternativ;
  if (!forhandsvisning && readonly && !ettvalgId) {
    return (
      <div className="Sporsmal-body Sporsmal-body--bold">
        <div>
          <FormattedMessage id="validation.velgEttAlternativ" />
        </div>
        <em className="u--italic">
          <FormattedMessage id="validation.intetAlternativerValgt" />
        </em>
      </div>
    );
  }

  const updateValue = (
    svarAlternativId: number,
    selected: boolean,
    egendefinert: string,
  ) => {
    field.onChange(
      selected
        ? [
            {
              flereValg: false,
              egendefinertSvar: egendefinert,
              svarAlternativId,
            },
          ]
        : [],
    );
  };

  const radios = svarAlternativ.map((alternativ) => {
    const isChecked = field.value.find(
      (spsa) => spsa.svarAlternativId === alternativ.id,
    );
    const svar =
      isChecked?.egendefinertSvar &&
      field.value.find((spsa) => spsa.svarAlternativId === alternativ.id)
        ?.egendefinertSvar;

    return (
      <SvarAlternativ
        key={alternativ.id}
        id={alternativ.id}
        type="radio"
        egendefinertSvar={svar ?? ""}
        obligatorisk={obligatorisk}
        alternativ={alternativ}
        svarAlternativ={svarAlternativ}
        error={field.error}
        message={field.message}
        disabled={readonly}
        selected={!!isChecked}
        onChange={(selected, egendefinert) =>
          updateValue(alternativ.id, selected, egendefinert)
        }
      />
    );
  });

  const radioClass = cls({
    "u--marginTop1": radios.length > 1,
  });

  return (
    <div className="Sporsmal-body">
      <Tooltip
        className="u--block"
        message={field.message}
        align="left"
        position="bottom"
        disabled={
          !field.error ||
          svarAlternativ
            .filter((sa) =>
              field.value.find((spsa) => spsa.svarAlternativId === sa.id),
            )
            .some((sa) => sa.erEgendefinert)
        }
        alwaysShow
      >
        {radios.length > 1 ? (
          <Obligatorisk isObligatorisk={obligatorisk}>
            <FormattedMessage id="validation.velgEttAlternativ" />
          </Obligatorisk>
        ) : null}
        <div className={radioClass}>{radios}</div>
      </Tooltip>
    </div>
  );
};

export default EttValg;
